import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, Animated } from 'react-native';
import StandupListItem from './StandupListItem';

interface StandupItem {
  modified_by: string;
  modified_at: string;
  id: string;
  title: string;
  is_submitted?: boolean;
  status?: string;
  statusColor?: string;
  onEditPress?: () => void;
  onViewPress?: () => void;
}

interface StandupListContainerProps {
  items: StandupItem[];
  maxVisibleItems?: number;
  onViewMorePress?: () => void;
  isLoading?: boolean;
}

// Skeleton component for loading state
const StandupListItemSkeleton: React.FC = () => {
  const opacity = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [opacity]);

  return (
    <View style={styles.skeletonItem}>
      <View style={styles.skeletonLeft}>
        <Animated.View style={[styles.skeletonTitle, { opacity }]} />
        <Animated.View style={[styles.skeletonSubtitle, { opacity }]} />
      </View>
      <View style={styles.skeletonRight}>
        <Animated.View style={[styles.skeletonButton, { opacity }]} />
      </View>
    </View>
  );
};

const StandupListContainer: React.FC<StandupListContainerProps> = ({
  items,
  maxVisibleItems = 4,
  onViewMorePress,
  isLoading = false,
}) => {
  // Calculate height so exactly `maxVisibleItems` are visible.
  const itemHeight = 72;
  const gap = 8;
  const contentPadding = 20 * 2;
  const scrollViewHeight = (maxVisibleItems * itemHeight) + ((maxVisibleItems - 1) * gap) + contentPadding;

  const handleViewMore = () => {
    if (onViewMorePress) {
      onViewMorePress();
    }
  };

  return (
    <View style={styles.container}>
      {isLoading ? (
        <ScrollView 
          style={[styles.scrollView, { maxHeight: scrollViewHeight }]}
          contentContainerStyle={styles.scrollContent}
        >
          {[...Array(maxVisibleItems)].map((_, index) => (
            <View key={`skeleton-${index}`} style={index > 0 ? styles.itemWrapper : undefined}>
              <StandupListItemSkeleton />
            </View>
          ))}
        </ScrollView>
      ) : (
        <>
          <ScrollView 
            style={[styles.scrollView, { maxHeight: scrollViewHeight }]}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
          >
            {items.map((item, index) => (
              <View key={item.id} style={index > 0 ? styles.itemWrapper : undefined}>
                <StandupListItem
                  title={item.title}
                  status={item.status}
                  statusColor={item.statusColor}
                  lastModified={item.modified_at}
                  modifiedBy={item.modified_by}
                  onEditPress={item.onEditPress}
                  onViewPress={item.onViewPress}
                  isSubmitted={item.is_submitted}
                />
              </View>
            ))}
          </ScrollView>

          {items.length > 0 && (
            <View style={styles.viewMoreContainer}>
              <TouchableOpacity
                style={styles.viewMoreButton}
                onPress={handleViewMore}
                activeOpacity={0.7}
              >
                <Text style={styles.viewMoreText}>View More</Text>
              </TouchableOpacity>
            </View>
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8, // Updated to match "Large" corner radius
    padding:24,
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: 'rgba(0, 0, 0, 0.1)',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 1,
        shadowRadius: 8,
      },
      android: {
        elevation: 3,
      },
      web: {
        // boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
      },
    }),
  },
  scrollView: {
    backgroundColor: '#FFFFFF',
  },
  scrollContent: {
    // padding: 20,
   // gap: 16,
  },
  itemWrapper: {
    marginTop: 8,
  },
  viewMoreContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: 12,
    paddingTop: 12,
    paddingHorizontal: 12,
    borderTopColor: '#E0E0E0',
    backgroundColor: '#FFFFFF',
  },
  viewMoreButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#004895',
    borderRadius: 9999,
    minWidth: 80,
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewMoreText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFFFFF',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
    textAlign: 'center',
  },
  messageContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#005CBC',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
  },
  skeletonItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    minHeight: 72,
  },
  skeletonLeft: {
    flex: 1,
    gap: 8,
  },
  skeletonTitle: {
    width: '60%',
    height: 20,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonSubtitle: {
    width: '40%',
    height: 16,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonRight: {
    flexDirection: 'row',
    gap: 8,
  },
  skeletonButton: {
    width: 60,
    height: 32,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
});

export default StandupListContainer;
